import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { SmileIcon, KebabIcon } from "../../icons";
import InfoIcon from "../../atoms/InfoIcon";
import EmojiPicker from "../EmojiPicker";
import MessageActionsButton from "../../atoms/MessageActionsButton";
import classNames from "../../../utils/classNames";
import "../MessageActionsPanel/index.scss";
import DropdownMenuFloat from "../DropdownMenuFloat";

export default function CommentActionsPanel({
  className = "",
  quickEmojis = ["✅", "👀", "🙌"],
  onQuickReact = undefined,
  onOpenEmojiPicker = undefined,
  onEmojiPickerOpenChange = undefined,
  onMore = undefined,
  onEdit = undefined,
  onDelete = undefined,
  showReactions = true,
}) {
  const { t } = useTranslation();
  const safeEmojis = useMemo(() => Array.isArray(quickEmojis) ? quickEmojis.slice(0, 3) : ["✅", "👀", "🙌"], [quickEmojis]);
  const [menu, setMenu] = useState({ open: false, x: 0, y: 0 });

  const closeMenu = useCallback(() => setMenu({ open: false, x: 0, y: 0 }), []);

  const hasMenu = Boolean(onEdit || onDelete);
  const shouldShowReactions = Boolean(showReactions);
  const shouldShowQuick = shouldShowReactions && safeEmojis.length > 0;

  return (
    <div
      className={classNames(
        "message-actions",
        "comment-actions",
        className,
        { "comment-actions--minimal": !shouldShowReactions }
      )}
      role="toolbar"
      aria-label="Comment actions"
    >
      {shouldShowQuick && (
        <div className="message-actions__quick" aria-label="Quick reactions">
          {safeEmojis.map((emoji) => (
            <MessageActionsButton
              key={emoji}
              className="message-actions__btn--quick"
              onClick={(e) => { e.stopPropagation(); onQuickReact?.(emoji); }}
              title={`React with ${emoji}`}
              ariaLabel={`React with ${emoji}`}
            >
              <span aria-hidden="true">{emoji}</span>
            </MessageActionsButton>
          ))}
        </div>
      )}
      {shouldShowReactions && <div className="message-actions__divider" aria-hidden="true" />}
      <div className={classNames("message-actions__main", { "message-actions__main--solo": !shouldShowReactions })}>
        {shouldShowReactions && (
          <InfoIcon text="Add a reaction" className="message-actions__tip">
            <div className="message-actions__emoji">
              <EmojiPicker
                onSelectEmoji={(emoji) => onQuickReact?.(emoji?.native || emoji)}
                onOpenChange={(open) => onEmojiPickerOpenChange?.(open)}
                iconAlt="Add reaction"
                overlayTrigger
              >
                <MessageActionsButton className="message-actions__btn--visual" title="React" ariaLabel="Add reaction" onClick={(e) => e.preventDefault()}>
                  <SmileIcon />
                  <span className="message-actions__label">React</span>
                </MessageActionsButton>
              </EmojiPicker>
            </div>
          </InfoIcon>
        )}
        {hasMenu && (
          <InfoIcon text="More actions" className="message-actions__tip">
            <MessageActionsButton
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget?.getBoundingClientRect?.();
                const x = rect ? rect.right : (e?.clientX || 0);
                const y = rect ? rect.bottom : (e?.clientY || 0);
                setMenu({ open: true, x, y });
                onMore?.(e);
              }}
              title="More actions"
              ariaLabel="More actions"
            >
              <KebabIcon />
            </MessageActionsButton>
          </InfoIcon>
        )}
      </div>
      {menu.open && hasMenu && (
        <DropdownMenuFloat
          position={{ x: menu.x, y: menu.y }}
          items={[
            ...(onEdit ? [{ label: t("actions.edit"), onClick: onEdit }] : []),
            ...(onDelete ? [{ label: t("actions.delete"), tone: "danger", onClick: onDelete }] : []),
          ]}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

CommentActionsPanel.propTypes = {
  className: PropTypes.string,
  quickEmojis: PropTypes.arrayOf(PropTypes.string),
  onQuickReact: PropTypes.func,
  onOpenEmojiPicker: PropTypes.func,
  onEmojiPickerOpenChange: PropTypes.func,
  onMore: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showReactions: PropTypes.bool,
};
